import React from "react";
import { Tooltip, Group, Text, Skeleton, Box, Flex } from "@mantine/core";
import { getNestedFieldValue } from "../../../ideaNiv/helpers/calculateImpactResults";
import { OverflownText } from "../../../OverflownText";
import { SourceIcon } from "../../../../utils/SourceIcon";
import { SparklesIcon } from "../../../../icons/x-symbol-svgrepo-com";

export type DeliveryStatusDisplayMode =
  | "combined"
  | "combinedWithLink"
  | "statusOnly"
  | "linkOnly";

const DISPLAY_MODES = new Set<string>([
  "combined",
  "combinedWithLink",
  "statusOnly",
  "linkOnly",
]);

function resolveDisplayMode(renderParams: Record<string, unknown> | undefined): DeliveryStatusDisplayMode {
  const dm = renderParams?.displayMode;
  if (typeof dm === "string" && DISPLAY_MODES.has(dm)) {
    return dm as DeliveryStatusDisplayMode;
  }
  return "combinedWithLink";
}

function formatDeliveryStatus(status: unknown): string {
  if (status == null) return "";
  return typeof status === "string" ? status : String(status);
}

function resolveCombinedStatusText(
  data: any,
  col: any,
  productItem: NonNullable<any>
): string {
  let value = getNestedFieldValue(data, col.accessor);
  // Migration-era: when product item status disagrees with idea field, prefer delivery.
  if (productItem && value !== productItem.status) {
    value = productItem.status;
  }
  const v = value ?? "";
  return typeof v === "string" ? v : String(v);
}

const outerBoxSx = {
  width: "100%",
  maxWidth: "100%",
  overflow: "hidden",
} as const;

function DeliveryStatusText({
  data,
  col,
  productItem,
  statusSource,
  tooltip,
}: {
  data: any;
  col: any;
  productItem: any | null | undefined;
  statusSource: "combined" | "delivery";
  tooltip?: string;
}) {
  const display =
    statusSource === "delivery"
      ? formatDeliveryStatus(productItem?.status)
      : productItem
        ? resolveCombinedStatusText(data, col, productItem)
        : String(getNestedFieldValue(data, col.accessor) ?? "");

  return (
    <OverflownText
      overTooltip={tooltip}
      sx={{
        flex: "1 1 auto",
        minWidth: 0,
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
      }}
    >
      {display}
    </OverflownText>
  );
}

function DeliveryItemLink({ productItem }: { productItem: any }) {
  return (
    <Tooltip label="Click to open" withArrow>
      <Group
        noWrap
        spacing={6}
        sx={{
          flex: "0 1 auto",
          minWidth: 0,
          overflow: "hidden",
          padding: "2px 4px",
          "&:hover": {
            backgroundColor: "#F8F8FB",
            borderRadius: "4px",
          },
        }}
      >
        <SourceIcon sourceName={productItem.origin} width={14} height={14} />
        <Text
          component="a"
          href={productItem.link}
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
          color="indigo"
          onClick={(e) => {
            e.stopPropagation();
          }}
          sx={{
            flex: "0 1 auto",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {productItem.itemKey ?? ""}
        </Text>
      </Group>
    </Tooltip>
  );
}

function NoProductItemFallback({
  data,
  col,
  isGenerated,
}: {
  data: any;
  col: any;
  isGenerated: boolean;
}) {
  const value = getNestedFieldValue(data, col.accessor);
  const label = isGenerated ? "AI Generated" : String(value ?? "");
  return (
    <Box sx={{ ...outerBoxSx }}>
      <Group noWrap spacing={6} sx={{ minWidth: 0, maxWidth: "100%" }}>
        <OverflownText
          sx={{
            flex: "1 1 auto",
            minWidth: 0,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
            color: isGenerated ? "#5B60E3" : undefined,
          }}
        >
          {label}
        </OverflownText>
        {isGenerated ? <SparklesIcon color="#5B60E3" /> : null}
      </Group>
    </Box>
  );
}

export const DeliveryStatusCell: React.FC<any> = ({
  data,
  col,
  renderParams = {},
}) => {
  if (data?._isThemeRow) {
    return <Box />;
  }

  const displayMode = resolveDisplayMode(renderParams);
  const productItem = data?.productItem;
  const isGenerated = col.accessor === "status" && data.state === "generated";
  const tooltip = renderParams.tooltip as string | undefined;

  if (!productItem) {
    if (displayMode === "linkOnly") {
      return <Box sx={{ ...outerBoxSx }} />;
    }
    return <NoProductItemFallback data={data} col={col} isGenerated={isGenerated} />;
  }

  switch (displayMode) {
  case "combined":
    return (
      <Box sx={{ ...outerBoxSx }}>
        <Group noWrap spacing={6} sx={{ minWidth: 0, maxWidth: "100%" }}>
          <DeliveryStatusText
            data={data}
            col={col}
            productItem={productItem}
            statusSource="combined"
            tooltip={tooltip}
          />
        </Group>
      </Box>
    );
  case "combinedWithLink":
    return (
      <Box sx={{ ...outerBoxSx }}>
        <Group noWrap spacing={6} sx={{ minWidth: 0, maxWidth: "100%" }}>
          <DeliveryStatusText
            data={data}
            col={col}
            productItem={productItem}
            statusSource="combined"
            tooltip={tooltip}
          />
          <DeliveryItemLink productItem={productItem} />
        </Group>
      </Box>
    );
  case "statusOnly":
    return (
      <Box sx={{ ...outerBoxSx }}>
        <Group noWrap spacing={6} sx={{ minWidth: 0, maxWidth: "100%" }}>
          <DeliveryStatusText
            data={data}
            col={col}
            productItem={productItem}
            statusSource="delivery"
            tooltip={tooltip}
          />
        </Group>
      </Box>
    );
  case "linkOnly":
    return (
      <Box sx={{ ...outerBoxSx }}>
        <Group noWrap spacing={6} sx={{ minWidth: 0, maxWidth: "100%" }}>
          <DeliveryItemLink productItem={productItem} />
        </Group>
      </Box>
    );
  default:
    return (
      <Box sx={{ ...outerBoxSx }}>
        <Group noWrap spacing={6} sx={{ minWidth: 0, maxWidth: "100%" }}>
          <DeliveryStatusText
            data={data}
            col={col}
            productItem={productItem}
            statusSource="combined"
            tooltip={tooltip}
          />
          <DeliveryItemLink productItem={productItem} />
        </Group>
      </Box>
    );
  }
};

export const DeliveryStatusCellSkeleton: React.FC<{ col: any }> = ({ col }) => {
  const columnWidth = col.width;
  const minWidth = col.minWidth;
  const maxWidth = col.maxWidth;

  const cw =
    typeof columnWidth === "number"
      ? columnWidth
      : parseInt(String(columnWidth ?? ""), 10);
  const statusWidth =
    Number.isFinite(cw) && cw > 0 ? Math.min(cw * 0.7, 100) : 80;

  return (
    <Box
      sx={{
        width: columnWidth || "100%",
        minWidth,
        maxWidth,
      }}
    >
      <Flex align="center" gap={4}>
        <Skeleton height={16} width={statusWidth} radius="sm" />
        <Skeleton height={14} width={14} radius="sm" />
      </Flex>
    </Box>
  );
};
